import { lazy, Suspense, useEffect } from 'react';
import { Route, Routes } from 'react-router-dom';
import './App.css';
import Navbar from './components/Navbar/Navbar';
import BottomToTop from './Animations/BottomToTop';
import Footer from './components/Footer/Footer';
import { ScrollToTop } from './Hooks/usePreventScrolling';
import { HelmetProvider } from 'react-helmet-async';

const Home = lazy(() => import('./pages/Home/Home'));
const ServicesHub = lazy(() => import('./pages/Services/ServicesHub'));
const ServiceDetail = lazy(() => import('./pages/Services/ServiceDetail'));
const IndustriesHub = lazy(() => import('./pages/Industries/IndustriesHub'));
const IndustryDetail = lazy(() => import('./pages/Industries/IndustryDetail'));
const Company = lazy(() => import('./pages/Company/Company'));
const Process = lazy(() => import('./pages/Process/Process'));
const Technology = lazy(() => import('./pages/Technology/Technology'));
const SiteMap = lazy(() => import('./pages/SiteMap/SiteMap'));
const Portfolio = lazy(() => import('./pages/Portfolio/Portfolio'));
const ProjectDetail = lazy(() => import('./pages/Portfolio/ProjectDetail'));
const BlogList = lazy(() => import('./pages/Blog/BlogList'));
const BlogPost = lazy(() => import('./pages/Blog/BlogPost'));
const LocationsHub = lazy(() => import('./pages/Locations/LocationsHub'));
const LocationDetail = lazy(() => import('./pages/Locations/LocationDetail'));
const CanadaLocationsHub = lazy(() => import('./pages/Locations/CanadaLocationsHub'));
const CanadaLocationDetail = lazy(() => import('./pages/Locations/CanadaLocationDetail'));
const EuropeLocationsHub = lazy(() => import('./pages/Locations/EuropeLocationsHub'));
const EuropeLocationDetail = lazy(() => import('./pages/Locations/EuropeLocationDetail'));
const Certificates = lazy(() => import('./pages/Certificates/Certificates'));
const CertificateDetail = lazy(() => import('./pages/Certificates/CertificateDetail'));
const ContactUs = lazy(() => import('./pages/ContactUs/ContactUs'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('./pages/TermsOfUse/TermsOfUse'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

function App() {
  useEffect(() => {
    let idleId;
    let timeoutId;

    const prefetchRoutes = () => {
      import('./pages/Portfolio/Portfolio');
      import('./pages/Services/ServicesHub');
      import('./pages/Industries/IndustriesHub');
      import('./pages/Process/Process');
      import('./pages/Technology/Technology');
      import('./pages/Blog/BlogList');
      import('./pages/ContactUs/ContactUs');
      import('./pages/Certificates/Certificates');
    };

    if ('requestIdleCallback' in window) {
      idleId = window.requestIdleCallback(prefetchRoutes, { timeout: 2000 });
    } else {
      timeoutId = window.setTimeout(prefetchRoutes, 1200);
    }

    return () => {
      if (idleId && 'cancelIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      }
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, []);

  return (
    <HelmetProvider>
      <div className="App" data-build="20260603-redirect-fix">
        <ScrollToTop />
        <BottomToTop fromY={100} delay={0.4} duration={0.4}>
          <Navbar />
        </BottomToTop>

        <Suspense fallback={<div aria-hidden="true" />}>
          <Routes>
            <Route path='/' element={<Home />} />
            <Route path='/services' element={<ServicesHub />} />
            <Route path='/services/:slug' element={<ServiceDetail />} />
            <Route path='/industries' element={<IndustriesHub />} />
            <Route path='/industries/:slug' element={<IndustryDetail />} />
            <Route path='/company' element={<Company />} />
            <Route path='/process' element={<Process />} />
            <Route path='/technology-stack' element={<Technology />} />
            <Route path='/sitemap' element={<SiteMap />} />
            <Route path='/portfolio' element={<Portfolio />} />
            <Route path='/portfolio/:slug' element={<ProjectDetail />} />
            <Route path='/blog' element={<BlogList />} />
            <Route path='/blog/:slug' element={<BlogPost />} />
            <Route path='/usa' element={<LocationsHub />} />
            <Route path='/usa/:slug' element={<LocationDetail />} />
            <Route path='/canada' element={<CanadaLocationsHub />} />
            <Route path='/canada/:slug' element={<CanadaLocationDetail />} />
            <Route path='/europe' element={<EuropeLocationsHub />} />
            <Route path='/europe/:slug' element={<EuropeLocationDetail />} />
            <Route path='/certificates' element={<Certificates />} />
            <Route path='/certificates/:slug' element={<CertificateDetail />} />
            <Route path='/contact-us' element={<ContactUs />} />
            <Route path='/privacy-policy' element={<PrivacyPolicy />} />
            <Route path='/terms-of-use' element={<TermsOfUse />} />
            <Route path='*' element={<NotFound />} />
          </Routes>
        </Suspense>

        <Footer />
      </div>
    </HelmetProvider>
  );
}

export default App;
